import styled from 'styled-components';
import { discreteScrollbar } from '../../themes/scrollbar';

// Panel lateral de estatisticas do LineChart em modo avancado. Layout
// vertical, scroll proprio quando conteudo passa do limite.
export const Container = styled.aside`
  width: 300px;
  flex-shrink: 0;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 12px 4px 16px;
  border-left: 1px solid ${(props) => props.theme.colors.disabled};
  font-variant-numeric: tabular-nums;
  ${discreteScrollbar}
`;

// =============================================================================
// HERO CARD — bloco de destaque no topo do panel. Mostra o valor central
// (media do periodo) em fonte grande + chip de tendencia. E a primeira
// coisa que o operador ve: "como ta a variavel AGORA, no geral?"
// =============================================================================
export const Hero = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 14px 12px;
  border-radius: 8px;
  background: ${(props) =>
    props.theme.title === 'light'
      ? 'rgba(59, 191, 241, 0.06)'
      : 'rgba(59, 191, 241, 0.08)'};
  border: 1px solid
    ${(props) =>
      props.theme.title === 'light'
        ? 'rgba(59, 191, 241, 0.25)'
        : 'rgba(59, 191, 241, 0.3)'};
`;

export const HeroLabel = styled.span`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${(props) => props.theme.colors.label};
  opacity: 0.85;
`;

export const HeroValue = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 28px;
  font-weight: 800;
  line-height: 1;
  color: ${(props) => props.theme.colors.darkerblue};

  small {
    font-size: 14px;
    font-weight: 600;
    color: ${(props) => props.theme.colors.label};
  }
`;

// Chip de tendencia: icon + label + magnitude. cor depende da direcao:
// laranja=subindo, azul=descendo, label=estavel.
export const TrendChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.2px;
  align-self: flex-start;
  background: ${(props) =>
    props.theme.title === 'light'
      ? `${props.color}1A`
      : `${props.color}26`};
  border: 1px solid ${(props) => `${props.color}66`};
  color: ${(props) => props.color};

  svg {
    width: 13px;
    height: 13px;
    stroke: currentColor;
    stroke-width: 2.5;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .magnitude {
    color: ${(props) => props.color};
    opacity: 0.8;
    font-weight: 600;
  }
`;

// =============================================================================
// GROUP — bloco generico. Cada um agrupa stats relacionadas.
// =============================================================================
export const Group = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const GroupTitle = styled.h3`
  margin: 0;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(props) => props.theme.colors.label};
  opacity: 0.7;
`;

// =============================================================================
// RANGE BAR — visualizacao da faixa min-max com Q1-Q3 destacado dentro.
// Substitui IQR + Q1/Q3 numericos por algo que o operador "ve" imediato.
// =============================================================================
export const RangeBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 6px;
  background: ${(props) =>
    props.theme.title === 'light'
      ? 'rgba(0,0,0,0.025)'
      : 'rgba(255,255,255,0.03)'};
`;

export const RangeBarTrack = styled.div`
  position: relative;
  height: 8px;
  border-radius: 4px;
  background: ${(props) =>
    props.theme.title === 'light'
      ? 'rgba(0,0,0,0.08)'
      : 'rgba(255,255,255,0.08)'};
`;

// "Caixa" dos quartis: regiao Q1-Q3 (onde estao 50% dos pontos centrais).
// Posicionada como porcentagem do span min-max.
export const RangeBarBox = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${(props) => props.$startPct}%;
  width: ${(props) => Math.max(0, props.$endPct - props.$startPct)}%;
  background: ${(props) => props.theme.colors.lightblue};
  opacity: 0.5;
  border-radius: 4px;
`;

// Marcador da mediana — linha vertical mais grossa dentro do box.
export const RangeBarMedian = styled.div`
  position: absolute;
  top: -2px;
  bottom: -2px;
  left: ${(props) => props.$pct}%;
  width: 3px;
  margin-left: -1.5px;
  background: ${(props) => props.theme.colors.darkblue};
  border-radius: 1.5px;
`;

export const RangeBarLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: ${(props) => props.theme.colors.label};
  font-weight: 600;

  strong {
    color: ${(props) => props.theme.colors.darkerblue};
    font-weight: 800;
  }
`;

export const RangeBarSpread = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font-size: 11px;
  color: ${(props) => props.theme.colors.label};

  strong {
    color: ${(props) => props.theme.colors.darkerblue};
    font-weight: 800;
    font-size: 12px;
  }
`;

// =============================================================================
// EXTREMOS — destaca min/max com timestamp em chip
// =============================================================================
export const ExtremeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  background: ${(props) =>
    props.theme.title === 'light'
      ? 'rgba(0,0,0,0.025)'
      : 'rgba(255,255,255,0.03)'};
`;

export const ExtremeIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.color};
  background: ${(props) => `${props.color}1A`};
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    stroke-width: 2.5;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

export const ExtremeInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
`;

export const ExtremeLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: ${(props) => props.theme.colors.label};
`;

export const ExtremeValueLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;

  strong {
    font-size: 15px;
    font-weight: 800;
    color: ${(props) => props.theme.colors.darkerblue};
  }
  small {
    font-size: 11px;
    color: ${(props) => props.theme.colors.label};
  }
  .when {
    font-size: 10px;
    color: ${(props) => props.theme.colors.label};
    margin-left: auto;
    font-weight: 600;
  }
`;

// =============================================================================
// ZONE STACKED BAR — barra horizontal unica mostrando proporcao das zonas
// =============================================================================
export const ZoneStackedBar = styled.div`
  display: flex;
  width: 100%;
  height: 14px;
  border-radius: 7px;
  overflow: hidden;
  background: ${(props) =>
    props.theme.title === 'light'
      ? 'rgba(0,0,0,0.06)'
      : 'rgba(255,255,255,0.06)'};
`;

export const ZoneStackedSegment = styled.div`
  height: 100%;
  width: ${(props) => Math.max(0, Math.min(100, props.$pct))}%;
  background: ${(props) => props.color};
  transition: width 220ms ease;
`;

export const ZoneLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: 4px;
`;

export const ZoneLegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
`;

export const ZoneDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${(props) => props.color};
  flex-shrink: 0;
`;

export const ZoneLegendLabel = styled.span`
  flex: 1;
  color: ${(props) => props.theme.colors.darkerblue};
  text-transform: capitalize;
  font-weight: 600;
`;

export const ZoneLegendValue = styled.span`
  font-weight: 800;
  color: ${(props) => props.theme.colors.darkerblue};
  font-size: 12px;
`;

export const ZoneLegendDuration = styled.span`
  font-size: 10px;
  color: ${(props) => props.theme.colors.label};
  font-style: normal;
  opacity: 0.8;
  min-width: 60px;
  text-align: right;
`;

// =============================================================================
// CORRELACAO ROW — mantida com leve refinamento + badge de intensidade
// =============================================================================
export const CorrRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 8px 10px;
  border-radius: 6px;
  background: ${(props) =>
    props.theme.title === 'light'
      ? 'rgba(0,0,0,0.025)'
      : 'rgba(255,255,255,0.03)'};
`;

export const CorrHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CorrLabel = styled.span`
  flex: 1;
  font-size: 12px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.darkerblue};
`;

export const CorrValue = styled.span`
  font-size: 13px;
  font-weight: 800;
  color: ${(props) => props.color || props.theme.colors.darkerblue};
  font-variant-numeric: tabular-nums;

  svg {
    width: 12px;
    height: 12px;
    margin-right: 2px;
    vertical-align: -1px;
    stroke: currentColor;
    stroke-width: 2.5;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

export const CorrBar = styled.div`
  width: 100%;
  height: 5px;
  border-radius: 3px;
  background: ${(props) =>
    props.theme.title === 'light'
      ? 'rgba(0,0,0,0.06)'
      : 'rgba(255,255,255,0.06)'};
  overflow: hidden;
`;

export const CorrBarFill = styled.div`
  height: 100%;
  width: ${(props) => Math.max(0, Math.min(100, props.$pct))}%;
  background: ${(props) => props.color};
  border-radius: 3px;
  transition: width 220ms ease;
`;

export const CorrBadge = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.label};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

// =============================================================================
// CHANGE POINT — linha visual com icone direcional + delta + timestamp
// =============================================================================
export const ChangeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  border-radius: 6px;
  background: ${(props) =>
    props.theme.title === 'light'
      ? 'rgba(0,0,0,0.025)'
      : 'rgba(255,255,255,0.03)'};
`;

export const ChangeIcon = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.color};
  background: ${(props) => `${props.color}1A`};
  flex-shrink: 0;

  svg {
    width: 12px;
    height: 12px;
    stroke: currentColor;
    stroke-width: 3;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

export const ChangeTime = styled.span`
  font-size: 11px;
  color: ${(props) => props.theme.colors.label};
  font-weight: 600;
  flex: 1;
  min-width: 0;
`;

export const ChangeDelta = styled.span`
  font-size: 12px;
  font-weight: 800;
  color: ${(props) => props.color};
  font-variant-numeric: tabular-nums;

  small {
    font-size: 0.8em;
    opacity: 0.7;
    margin-left: 2px;
  }
`;

// =============================================================================
// LINHAS GENERICAS — pra Disponibilidade, Anomalias, Anotacoes, Janela.
// Estilo mais compacto que antes. Padding menor.
// =============================================================================
export const Row = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  padding: 3px 0;
`;

export const Label = styled.span`
  font-size: 11px;
  color: ${(props) => props.theme.colors.label};
  font-weight: 500;
`;

export const Value = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${(props) => props.color || props.theme.colors.darkerblue};
  text-align: right;

  small {
    font-size: 0.75em;
    font-weight: 600;
    color: ${(props) => props.theme.colors.label};
    margin-left: 3px;
  }
`;

// Card "callout" pra Anomalias — alerta visual quando ha outliers.
export const AnomalyCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 6px;
  background: ${(props) =>
    props.theme.title === 'light'
      ? `${props.theme.colors.red}10`
      : `${props.theme.colors.red}1A`};
  border: 1px solid ${(props) => `${props.theme.colors.red}66`};
`;

export const AnomalyIcon = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.colors.red};
  background: ${(props) => `${props.theme.colors.red}26`};
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    stroke-width: 2.5;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

export const AnomalyText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
`;

export const AnomalyCount = styled.strong`
  font-size: 14px;
  font-weight: 800;
  color: ${(props) => props.theme.colors.red};
`;

export const AnomalyBreakdown = styled.span`
  font-size: 10px;
  color: ${(props) => props.theme.colors.label};
  font-weight: 600;
`;

// Hint em pe de cada secao
export const Hint = styled.span`
  font-size: 10px;
  color: ${(props) => props.theme.colors.label};
  opacity: 0.7;
`;

export const EmptyMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 20px;
  font-size: 12px;
  color: ${(props) => props.theme.colors.label};
  font-style: italic;
  text-align: center;
  opacity: 0.7;
`;

// =============================================================================
// ANOTACAO — keep visual elegante. Item de lista com timestamp + texto.
// =============================================================================
export const NoteItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 6px;
  background: ${(props) =>
    props.theme.title === 'light'
      ? 'rgba(0,0,0,0.025)'
      : 'rgba(255,255,255,0.03)'};
`;

export const NoteIcon = styled.div`
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.colors.yellow || '#fbbf24'};
  flex-shrink: 0;
  margin-top: 1px;

  svg {
    width: 13px;
    height: 13px;
    stroke: currentColor;
    stroke-width: 2.2;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

export const NoteBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

export const NoteTime = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.yellow || '#fbbf24'};
`;

export const NoteText = styled.span`
  font-size: 11px;
  color: ${(props) => props.theme.colors.darkerblue};
  word-break: break-word;
  line-height: 1.35;
`;

export const NoteDelete = styled.button`
  background: transparent;
  border: none;
  color: ${(props) => props.theme.colors.label};
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  font-size: 16px;
  opacity: 0.7;
  transition: color 120ms, opacity 120ms;

  &:hover {
    color: ${(props) => props.theme.colors.red};
    opacity: 1;
  }
`;

// =============================================================================
// FOOTER (Janela) — metadado discreto no fim
// =============================================================================
export const FooterMeta = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 10px;
  border-top: 1px solid
    ${(props) =>
      props.theme.title === 'light'
        ? 'rgba(0,0,0,0.06)'
        : 'rgba(255,255,255,0.06)'};
  font-size: 10px;
  color: ${(props) => props.theme.colors.label};
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
`;

// =============================================================================
// UPTIME BIG VALUE — destaca o % grande, como hero secundario.
// =============================================================================
export const UptimeBig = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-size: 22px;
  font-weight: 800;
  line-height: 1;
  color: ${(props) => props.color || props.theme.colors.green};

  small {
    font-size: 12px;
    font-weight: 700;
    opacity: 0.8;
  }
`;

// Para retrocompat se algo importar — alias pra Row antigo.
export const ZoneRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 0;
`;

export const ZoneHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const ZoneLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.darkerblue};
  text-transform: capitalize;
  flex: 1;
`;

export const ZonePct = styled.span`
  font-size: 12px;
  font-weight: 800;
  color: ${(props) => props.theme.colors.darkerblue};
`;

export const ZoneBar = styled.div`
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: ${(props) =>
    props.theme.title === 'light'
      ? 'rgba(0,0,0,0.06)'
      : 'rgba(255,255,255,0.06)'};
  overflow: hidden;
`;

export const ZoneBarFill = styled.div`
  height: 100%;
  width: ${(props) => Math.max(0, Math.min(100, props.pct))}%;
  background: ${(props) => props.color};
  border-radius: 2px;
  transition: width 220ms ease;
`;

